import { useState, useEffect } from 'react';

/**
 * Device detection hook for responsive design
 * Breakpoints:
 * - Mobile: < 768px
 * - Tablet: 768px - 1024px
 * - Desktop: > 1024px
 */
const useDeviceDetection = () => {
  // Check if we're in the browser
  const isBrowser = typeof window !== 'undefined';
  
  // Get initial values from window if available
  const getInitialDevice = () => {
    if (!isBrowser) {
      return {
        isMobile: false,
        isTablet: false,
        isDesktop: true,
        screenWidth: 1200,
        screenHeight: 800,
        orientation: 'landscape',
        isTouchDevice: false,
      };
    }
    
    const width = window.innerWidth;
    const height = window.innerHeight;
    const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    
    return {
      isMobile: width < 768,
      isTablet: width >= 768 && width < 1024,
      isDesktop: width >= 1024,
      screenWidth: width,
      screenHeight: height,
      orientation: width > height ? 'landscape' : 'portrait',
      isTouchDevice,
    };
  };

  const [device, setDevice] = useState(getInitialDevice);

  useEffect(() => {
    if (!isBrowser) return;

    const checkDevice = () => {
      const width = window.innerWidth;
      const height = window.innerHeight;
      const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
      
      setDevice({
        isMobile: width < 768,
        isTablet: width >= 768 && width < 1024,
        isDesktop: width >= 1024,
        screenWidth: width,
        screenHeight: height,
        orientation: width > height ? 'landscape' : 'portrait',
        isTouchDevice,
      });
    };

    // Initial check - run immediately
    checkDevice();

    // Listen for resize events
    window.addEventListener('resize', checkDevice);
    window.addEventListener('orientationchange', checkDevice);
    
    // iOS Safari specific: Also listen for viewport changes
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', checkDevice);
    }

    return () => {
      window.removeEventListener('resize', checkDevice);
      window.removeEventListener('orientationchange', checkDevice);
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', checkDevice);
      }
    };
  }, [isBrowser]);

  return device;
};

export default useDeviceDetection;
